// Dialogs (Radix). `SimpleDialog` and `ConfirmationDialog` are drop-in replacements for
// components/Dialog/SimpleDialog and ConfirmationDialog.
import { Dialog as D } from "radix-ui";
import { X } from "lucide-react";
import { cn } from "./cn";
import { Button } from "./Button";

export function Dialog({ open, onClose, title, description, children, footer, className, size = "md", style }) {
  const widths = { sm: "max-w-md", md: "max-w-xl", lg: "max-w-3xl", xl: "max-w-5xl" };
  // Non-modal while MUI is still on the page: a modal Radix dialog blocks pointer events outside itself,
  // which would make MUI menus/autocompletes (portalled to <body>) inside the dialog unclickable.
  // Like the old MUI dialogs, clicking the backdrop does not close it; Escape and the close button do.
  return (
    <D.Root open={!!open} modal={false} onOpenChange={(o) => !o && onClose?.()}>
      <D.Portal>
        {open && <div aria-hidden className="fixed inset-0 z-[1300] bg-[#111114]/35 backdrop-blur-[2px]" />}
        <D.Content
          role="dialog"
          aria-modal="true"
          onInteractOutside={(e) => e.preventDefault()}
          className={cn(
            "fixed top-1/2 left-1/2 z-[1300] flex max-h-[90vh] w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-2xl border border-line bg-panel shadow-[0_24px_64px_-12px_rgba(16,17,20,0.28)] outline-none",
            widths[size] || size,
            className,
          )}
          style={style}
        >
          <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
            <div className="min-w-0">
              <D.Title className="m-0 text-[16px] font-semibold text-fg">{title}</D.Title>
              {description ? <D.Description className="m-0 mt-1 text-[13px] text-muted">{description}</D.Description> : <D.Description className="sr-only">{title}</D.Description>}
            </div>
            <D.Close asChild>
              <button type="button" aria-label="Close" className="-mr-1 grid size-8 shrink-0 place-items-center rounded-lg text-faint hover:bg-black/[0.045] hover:text-fg">
                <X className="size-4" />
              </button>
            </D.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && <div className="flex justify-end gap-2 border-t border-line px-5 py-3">{footer}</div>}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

/** Same props as components/Dialog/SimpleDialog: open, onClose, title, children, width. */
export function SimpleDialog({ open, onClose, title, children, width }) {
  return (
    <Dialog open={open} onClose={onClose} title={title} size={width ? "xl" : "md"} style={width ? { width: typeof width === "object" ? width.md ?? width.sm ?? width.xs : width } : undefined}>
      {children}
    </Dialog>
  );
}

/** Same props as components/Dialog/ConfirmationDialog: open, onClose, message, action. */
export function ConfirmationDialog({ open, onClose, message, action, confirmLabel = "Yes, continue", tone = "danger", loading }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Please confirm"
      size="sm"
      footer={
        <>
          <Button onClick={() => onClose?.()}>Cancel</Button>
          <Button variant={tone === "danger" ? "danger" : "primary"} loading={loading} onClick={() => action?.()}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="m-0 text-[14px] text-text">{message}</p>
    </Dialog>
  );
}
